import {
  AHIcon,
  AHIconType,
  AHInput,
  AHText,
  Box,
  ColorType,
  Image,
  ImageProps,
  TouchableBox,
  WebAutoGrowingTextArea,
  useAHTextTypeStyles,
  useCircleSize,
  useLayoutContext,
  useSpaceSize,
  useTheme,
} from "arkh";
import React, { useCallback } from "react";
import { Platform, TextInput } from "react-native";
import {
  AHImageCollectionListItemWrapper,
  AHImageHorizontalCollectionStaticSize,
} from ".";
import {
  useOpenCamera,
  useOpenCameraWithPromise,
  useOpenImagePicker,
  useOpenImagePickerWithPromise,
} from "./mobile/useImageActionSheet";
import { useIntlTokens } from "./mobile/useIntlTokens";
import {
  FileCollection,
  FileCollectionItem,
  FileItemType,
} from "./FileCollection";

import { useLightboxControls } from "../common/lightbox/LightboxProvider";
import { BottomSheetTextInput } from "@gorhom/bottom-sheet";

export function RichDescriptionInputButton(props: {
  type: AHIconType;
  onPress: () => void;
  tintColor?: ColorType;
  backgroundColor?: ColorType;
  disabled?: boolean;
}) {
  const size = useCircleSize("medium");
  const medium = useSpaceSize("medium");
  return (
    <TouchableBox
      disabled={props.disabled}
      hitSlop={{
        top: medium,
        bottom: medium,
        left: medium / 2,
        right: medium / 2,
      }}
      pressRetentionOffset={{
        top: medium,
        bottom: medium,
        left: medium / 2,
        right: medium / 2,
      }}
      cornerRadius="medium"
      color={props.backgroundColor ?? "surface-backgroundControl"}
      style={{
        height: size,
        width: size,
        justifyContent: "center",
        alignItems: "center",
      }}
      onPress={props.onPress}
    >
      <AHIcon
        type={props.type}
        size="small"
        color={props.tintColor ?? "text-washed"}
      />
    </TouchableBox>
  );
}

export function RichDescriptionInputFiles(props: {
  files: FileItemType[];
  onShowFile: (file: FileItemType) => void;
  onRemoveFile?: (file: FileItemType) => void;
  readOnly: boolean;
}) {
  if (props.files.length === 0 || !props.onShowFile) {
    return null;
  }

  return (
    <FileCollection>
      {props.files.map((file) => (
        <FileCollectionItem
          key={file.id}
          file={file}
          onShowFile={props.onShowFile!}
          onRemove={
            props.onRemoveFile && !props.readOnly
              ? () => props.onRemoveFile!(file)
              : undefined
          }
        />
      ))}
    </FileCollection>
  );
}

export function RichDescriptionInputMedia(props: {
  media: {
    width: number;
    height: number;
    image: ImageProps;
    onDelete?: () => void;
  }[];
}) {
  const { openLightbox } = useLightboxControls();

  if (props.media.length === 0) {
    return null;
  }
  return (
    <AHImageHorizontalCollectionStaticSize size={140} space="medium">
      {props.media.map((media, i) => {
        return (
          <AHImageCollectionListItemWrapper
            key={
              media.image.type === "local"
                ? media.image.uri
                : media.image.image?.id
            }
            onPress={() => {
              openLightbox({
                index: i,
                images: props.media.map((m) => ({
                  onDelete: m.onDelete,
                  mediaType: "image",
                  image: m.image,
                  dimensions: {
                    width: m.width,
                    height: m.height,
                  },
                  thumbnail: undefined,
                })),
              });
            }}
            shadow="none"
          >
            <Image style={{ flex: 1 }} {...media.image} />
          </AHImageCollectionListItemWrapper>
        );
      })}
    </AHImageHorizontalCollectionStaticSize>
  );
}

export function RichDescriptionInputControlls(props: {
  addFile?: () => void;
  highlightCamera?: boolean;
  onMediaAdd: (media: { uri: string; width: number; height: number }) => void;
}) {
  const onMediaAdd = props.onMediaAdd;
  const onAddMedia = useCallback(
    (uri: string, media: { uri: string; width: number; height: number }) => {
      onMediaAdd(media);
    },
    [onMediaAdd]
  );
  const { openCamera } = useOpenCamera({
    onAddImage: onAddMedia,
    options: {
      mediaTypes: ["images" as const],
      allowsEditing: false,
      quality: 1,
    },
  });
  const { openImagePicker } = useOpenImagePicker({
    onAddImage: onAddMedia,
    options: {
      mediaTypes: ["images" as const],
      allowsEditing: false,
      quality: 1,
    },
  });

  return (
    <RichDescriptionInputControllsComponent
      openImagePicker={openImagePicker}
      openCamera={openCamera}
      addFile={props.addFile}
      highlightCamera={props.highlightCamera}
    />
  );
}

export function RichDescriptionInputControllsWithPrepare(props: {
  addFile?: () => void;
  highlightCamera?: boolean;
  onMediaAdd: (media: {
    uri: string;
    width: number;
    height: number;
    imageId: string;
    uploadURL: string;
  }) => void;
  onPrepareUploadLink: () => Promise<{
    imageId: string;
    uploadURL: string;
  }>;
}) {
  const onPrepareUploadLink = props.onPrepareUploadLink;
  const onMediaAdd = props.onMediaAdd;

  const openCamera = useOpenCameraWithPromise({
    options: {
      mediaTypes: ["images" as const],
      allowsEditing: false,
      quality: 1,
    },
  });
  const openImagePicker = useOpenImagePickerWithPromise({
    options: {
      mediaTypes: ["images" as const],
      allowsEditing: false,
      quality: 1,
    },
  });
  const openCameraAction = useCallback(async () => {
    const prepareUploadLinkPromise = onPrepareUploadLink();
    const cameraResponse = await openCamera();
    if (!cameraResponse) {
      return;
    }
    const { imageId, uploadURL } = await prepareUploadLinkPromise;
    onMediaAdd({
      uri: cameraResponse.uri,
      width: cameraResponse.width,
      height: cameraResponse.height,
      imageId,
      uploadURL,
    });
  }, [openCamera, onPrepareUploadLink, onMediaAdd]);

  const openImagePickerAction = useCallback(async () => {
    const prepareUploadLinkPromise = onPrepareUploadLink();
    const imagePickerResponse = await openImagePicker();
    if (!imagePickerResponse) {
      return;
    }
    const { imageId, uploadURL } = await prepareUploadLinkPromise;
    onMediaAdd({
      uri: imagePickerResponse.uri,
      width: imagePickerResponse.width,
      height: imagePickerResponse.height,
      imageId,
      uploadURL,
    });
  }, [openImagePicker, onPrepareUploadLink, onMediaAdd]);

  return (
    <RichDescriptionInputControllsComponent
      openImagePicker={openImagePickerAction}
      openCamera={openCameraAction}
      addFile={props.addFile}
      highlightCamera={props.highlightCamera}
    />
  );
}

function RichDescriptionInputControllsComponent(props: {
  addFile?: () => void;
  highlightCamera?: boolean;
  openImagePicker: () => void;
  openCamera: () => void;
}) {
  return (
    <>
      <Box
        style={{
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "flex-end",
        }}
        spaceHorizontal="medium"
        spaceBottom="medium"
      >
        {props.addFile ? (
          <Box spaceRight="medium">
            <RichDescriptionInputButton type="files" onPress={props.addFile} />
          </Box>
        ) : null}

        <RichDescriptionInputButton
          type="image"
          onPress={props.openImagePicker}
        />

        {Platform.OS !== "web" ? (
          <Box spaceLeft="medium">
            <RichDescriptionInputButton
              type="camera"
              tintColor={props.highlightCamera ? "text-inverted" : undefined}
              backgroundColor={props.highlightCamera ? "text-cta" : undefined}
              onPress={props.openCamera}
            />
          </Box>
        ) : null}
      </Box>
    </>
  );
}

const DescriptionInputMobile = (props: {
  placeholder?: string;
  description: string;
  setDescription?(description: string): void;
  inBottomSheet?: boolean;
}) => {
  const intl_tokens = useIntlTokens();
  const layout = useLayoutContext();
  const inputTextStyle = useAHTextTypeStyles("body");

  const theme = useTheme();

  const InnerTextInput = props.inBottomSheet ? BottomSheetTextInput : TextInput;
  return (
    <Box space="small">
      <InnerTextInput
        style={{
          padding: layout.space.small,
          width: "100%",
          ...inputTextStyle,
        }}
        multiline
        autoCapitalize="sentences"
        scrollEnabled={false}
        value={props.description}
        onChangeText={props.setDescription}
        editable={props.setDescription !== undefined}
        placeholder={props.placeholder ?? intl_tokens.Description}
        placeholderTextColor={theme["text-washed"]}
      />
    </Box>
  );
};

const DescriptionInputWeb = (props: {
  placeholder?: string;
  description: string;
  setDescription?(description: string): void;
}) => {
  const intl_tokens = useIntlTokens();
  if (props.setDescription === undefined) {
    return (
      <Box space="small">
        <AHText type="body-big" space="small">
          {props.description}
        </AHText>
      </Box>
    );
  }
  return (
    <Box space="small">
      <WebAutoGrowingTextArea
        style={{ flex: 1, overflow: "hidden" }}
        placeholder={props.placeholder ?? intl_tokens.Description}
        space="small"
        cornerRadius="medium"
        textType="body-big"
        autoCapitalize="sentences"
        multiline
        value={props.description}
        onChangeText={props.setDescription}
        editable={props.setDescription !== undefined}
      />
    </Box>
  );
};

const InnerDescriptionInput = Platform.select({
  default: DescriptionInputMobile,
  web: DescriptionInputWeb,
});

export function RichDescriptionInputText(props: {
  description: string;
  setDescription?(description: string): void;
  readOnly: boolean;
}) {
  if (
    props.description.length > 0 ||
    (props.setDescription && !props.readOnly)
  ) {
    return (
      <InnerDescriptionInput
        {...props}
        setDescription={props.readOnly ? undefined : props.setDescription}
      />
    );
  }

  return null;
}

const TitleInputMobile = (props: {
  placeholder?: string;
  title: string;
  setTitle(title: string): void;
  disabled?: boolean;
  autoFocus?: boolean;
}) => {
  const intl_tokens = useIntlTokens();

  const small = useSpaceSize("small");
  return (
    <Box space="small">
      <AHInput
        autoFocus={props.autoFocus}
        style={{ flex: 1 }}
        hitSlop={{
          top: small,
          bottom: small,
          left: small,
          right: small,
        }}
        placeholder={props.placeholder ?? intl_tokens.Title}
        space="small"
        cornerRadius="medium"
        textType="title-small"
        scrollEnabled={false}
        returnKeyType="next"
        blurOnSubmit
        autoCapitalize="sentences"
        multiline
        value={props.title}
        onChangeText={props.setTitle}
        editable={!props.disabled}
      />
    </Box>
  );
};

const TitleInputWeb = (props: {
  placeholder?: string;
  title: string;
  setTitle(title: string): void;
  disabled?: boolean;
  autoFocus?: boolean;
}) => {
  const intl_tokens = useIntlTokens();
  if (props.setTitle === undefined || props.disabled) {
    return (
      <Box space="small">
        <AHText type="title-small" space="small">
          {props.title}
        </AHText>
      </Box>
    );
  }
  return (
    <Box space="small">
      <WebAutoGrowingTextArea
        style={{ flex: 1, overflow: "hidden" }}
        placeholder={props.placeholder ?? intl_tokens.Title}
        space="small"
        cornerRadius="medium"
        textType="title-small"
        autoCapitalize="sentences"
        autoFocus={props.autoFocus}
        value={props.title}
        onChangeText={(t) => {
          props.setTitle(t);
        }}
        editable={!props.disabled}
      />
    </Box>
  );
};

export const TitleInput = Platform.select({
  default: TitleInputMobile,
  web: TitleInputWeb,
});
